import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { CommandVocabularyCatalogEntry } from "./command-vocabulary-catalog";
import type {
  CommandVocabularyArticulation,
  CommandVocabularyEntry,
  CommandVocabularyHandle,
  CommandVocabularyRegister,
} from "../content/command-vocabulary";
import { SelectableProse } from "./selectable-prose";

type WordDiscoveryProps = {
  entries: CommandVocabularyCatalogEntry[];
  activeId: string;
  activeEntry: CommandVocabularyEntry | null;
  activeRelatedWord: string | null;
  query: string;
  initial: string;
  handle: CommandVocabularyHandle | "all";
  level: CommandVocabularyCatalogEntry["difficulty"] | "all";
  register: CommandVocabularyRegister | "all";
  articulation: CommandVocabularyArticulation | "all";
  loading: boolean;
  loadError: string;
  plus: boolean;
  saved: boolean;
  contextSaved: boolean;
  onSelect: (id: string, relatedWord?: string | null) => void;
  onQueryChange: (query: string, nextActiveId: string) => void;
  onCriteriaChange: (criteria: WordDiscoveryCriteria, nextActiveId: string) => void;
  onSave: (entry: CommandVocabularyEntry) => void;
  onPractice: (entry: CommandVocabularyEntry) => void;
  onHear: (entry: CommandVocabularyEntry) => void;
  onOpenPronunciationGuide: () => void;
  onShowPlus: () => void;
};

export type WordDiscoveryCriteria = {
  initial: string;
  handle: CommandVocabularyHandle | "all";
  level: CommandVocabularyCatalogEntry["difficulty"] | "all";
  register: CommandVocabularyRegister | "all";
  articulation: CommandVocabularyArticulation | "all";
};

const HANDLES: Array<{ id: CommandVocabularyHandle | "all"; label: string }> = [
  { id: "all", label: "Surprise me" },
  { id: "think-precisely", label: "Think precisely" },
  { id: "speak-with-confidence", label: "Speak with confidence" },
  { id: "understand-people", label: "Understand people" },
  { id: "write-memorably", label: "Write memorably" },
];

const LEVELS: Array<{ id: CommandVocabularyCatalogEntry["difficulty"] | "all"; label: string }> = [
  { id: "all", label: "Any familiarity" },
  { id: "foundation", label: "Foundation" },
  { id: "bright-frontier", label: "Recognisable" },
  { id: "mid-frontier", label: "Elevated" },
  { id: "deep-frontier", label: "Rare but live" },
];

const REGISTERS: Array<{ id: CommandVocabularyRegister | "all"; label: string }> = [
  { id: "all", label: "Any register" },
  { id: "conversational", label: "Conversation" },
  { id: "professional", label: "Professional" },
  { id: "formal", label: "Formal" },
  { id: "literary", label: "Literary" },
  { id: "rhetorical", label: "Rhetorical" },
];

const ARTICULATIONS: Array<{ id: CommandVocabularyArticulation | "all"; label: string }> = [
  { id: "all", label: "Any landing" },
  { id: "warm-authority", label: "Warm authority" },
  { id: "tactful", label: "Tactful" },
  { id: "restrained", label: "Restrained" },
  { id: "direct", label: "Direct" },
  { id: "punchy", label: "Punchy" },
];

const INITIALS = [
  { id: "all", label: "All" },
  ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((letter) => ({ id: letter, label: letter })),
];

export function WordDiscovery({
  entries,
  activeId,
  activeEntry,
  activeRelatedWord,
  query,
  initial,
  handle,
  level,
  register,
  articulation,
  loading,
  loadError,
  plus,
  saved,
  contextSaved,
  onSelect,
  onQueryChange,
  onCriteriaChange,
  onSave,
  onPractice,
  onHear,
  onOpenPronunciationGuide,
  onShowPlus,
}: WordDiscoveryProps) {
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [wordContextPinned, setWordContextPinned] = useState(false);
  const wordHeaderRef = useRef<HTMLElement | null>(null);
  const wordContextPinRef = useRef<HTMLDivElement | null>(null);
  const criteria = useMemo(
    () => ({ initial, handle, level, register, articulation }),
    [articulation, handle, initial, level, register],
  );
  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return entries.filter((entry) =>
      matchesCriteria(entry, criteria)
      && (!normalizedQuery
        || entry.word.includes(normalizedQuery)
        || entry.relatedWords.some((word) => word.includes(normalizedQuery))
        || entry.partOfSpeech.toLocaleLowerCase().includes(normalizedQuery))
    );
  }, [criteria, entries, query]);
  const active = filtered.find((entry) => entry.id === activeId)
    || entries.find((entry) => entry.id === activeId)
    || filtered[0]
    || entries[0];
  const navigationEntries = filtered.length ? filtered : entries;
  const activeIndex = active ? navigationEntries.findIndex((entry) => entry.id === active.id) : -1;
  const locked = Boolean(active && active.access === "plus" && !plus);

  useEffect(() => {
    let animationFrame = 0;
    const syncWordContextPin = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const header = wordHeaderRef.current;
        const pin = wordContextPinRef.current;
        if (!header || !pin) {
          setWordContextPinned(false);
          return;
        }
        const topbarBottom = document.querySelector<HTMLElement>(".topbar")?.getBoundingClientRect().bottom || 0;
        const practiceMapBounds = document.querySelector<HTMLElement>(".practice-stage > .practice-map")?.getBoundingClientRect();
        const practiceMapBottom = practiceMapBounds && practiceMapBounds.bottom > topbarBottom
          ? practiceMapBounds.bottom
          : topbarBottom;
        pin.style.setProperty("--word-context-top", `${Math.max(topbarBottom, practiceMapBottom)}px`);
        setWordContextPinned(header.getBoundingClientRect().bottom <= practiceMapBottom + 2);
      });
    };
    syncWordContextPin();
    window.addEventListener("scroll", syncWordContextPin, { passive: true });
    window.addEventListener("resize", syncWordContextPin);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", syncWordContextPin);
      window.removeEventListener("resize", syncWordContextPin);
    };
  }, [active.id]);

  const move = (step: number) => {
    if (!navigationEntries.length) return;
    const current = Math.max(0, activeIndex);
    const next = (current + step + navigationEntries.length) % navigationEntries.length;
    onSelect(navigationEntries[next].id, null);
  };

  const chooseCriteria = (next: WordDiscoveryCriteria) => {
    const nextEntries = entries.filter((entry) => matchesCriteria(entry, next));
    const nextActiveId = nextEntries.some((entry) => entry.id === activeId)
      ? activeId
      : nextEntries[0]?.id || activeId;
    onCriteriaChange(next, nextActiveId);
  };

  const changeQuery = (value: string) => {
    const normalizedQuery = value.trim().toLocaleLowerCase();
    const nextEntries = entries.filter((entry) =>
      matchesCriteria(entry, criteria)
      && (!normalizedQuery
        || entry.word.includes(normalizedQuery)
        || entry.relatedWords.some((word) => word.includes(normalizedQuery))
        || entry.partOfSpeech.toLocaleLowerCase().includes(normalizedQuery))
    );
    const nextActiveId = nextEntries.some((entry) => entry.id === activeId)
      ? activeId
      : nextEntries[0]?.id || activeId;
    onQueryChange(value.slice(0, 80), nextActiveId);
  };

  const finishSwipe = (event: ReactPointerEvent<HTMLElement>) => {
    if (!touchStart || event.pointerType !== "touch") return;
    const x = event.clientX - touchStart.x;
    const y = event.clientY - touchStart.y;
    setTouchStart(null);
    if (Math.abs(x) < 56 || Math.abs(x) < Math.abs(y) * 1.25) return;
    move(x < 0 ? 1 : -1);
  };

  if (!active) return null;

  return (
    <section className="word-discovery-stage" aria-labelledby="word-discovery-title">
      <div className="word-discovery-toolbar">
        <span><i /> {entries.filter((entry) => entry.access === "free").length} words included in Free</span>
        <strong>{entries.length} maintained now</strong>
      </div>
      <article className="word-discovery-workspace">
        <details className="word-discovery-browser">
          <summary>
            <strong>Browse or find a word</strong>
            <span>Search by idea, purpose, familiarity, setting, or landing.</span>
          </summary>
          <div className="word-discovery-controls">
            <nav aria-label="Choose what a word should help you do">
              {HANDLES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={handle === item.id ? "active" : ""}
                  aria-pressed={handle === item.id}
                  onClick={() => chooseCriteria({ ...criteria, handle: item.id })}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <label>
              <span>Find a word or idea</span>
              <input type="search" value={query} onChange={(event) => changeQuery(event.target.value)} maxLength={80} placeholder="Try: tact, precision, memorable…" />
            </label>
            <details className="word-discovery-particular">
              <summary>Be more particular</summary>
              <div>
                <ChoiceRow className="word-initial-browser" label="Start with a letter" choices={INITIALS} value={initial} onChoose={(value) => chooseCriteria({ ...criteria, initial: value })} />
                <ChoiceRow label="How familiar should it feel?" choices={LEVELS} value={level} onChoose={(value) => chooseCriteria({ ...criteria, level: value })} />
                <ChoiceRow label="Where should it belong?" choices={REGISTERS} value={register} onChoose={(value) => chooseCriteria({ ...criteria, register: value })} />
                <ChoiceRow label="How should it land?" choices={ARTICULATIONS} value={articulation} onChoose={(value) => chooseCriteria({ ...criteria, articulation: value })} />
              </div>
            </details>
          </div>

          {query.trim() && filtered.length ? (
            <nav className="word-search-results" aria-label="Matching words">
              {filtered.slice(0, 8).map((entry) => {
                const normalizedQuery = query.trim().toLocaleLowerCase();
                const relatedMatch = entry.relatedWords.find((word) => word.includes(normalizedQuery)) || null;
                return (
                  <button key={entry.id} type="button" onClick={() => onSelect(entry.id, relatedMatch)} className={entry.id === active.id ? "active" : ""}>
                    <strong>{relatedMatch || entry.word}</strong>
                    <span>{relatedMatch ? `In the ${entry.word} family` : `${entry.partOfSpeech} · ${levelLabel(entry.difficulty)}`}</span>
                  </button>
                );
              })}
            </nav>
          ) : null}

          {!filtered.length ? (
            <div className="word-discovery-empty" role="status">
              <strong>No maintained word matches every choice.</strong>
              <p>Loosen one choice and your place will remain here.</p>
            </div>
          ) : null}
        </details>

        <nav className="word-step-navigation" aria-label="Move through words">
          <button type="button" onClick={() => move(-1)} aria-label="Previous word">← <span>Previous</span></button>
          <strong>{Math.max(0, activeIndex) + 1} of {navigationEntries.length}</strong>
          <button type="button" onClick={() => move(1)} aria-label="Next word"><span>Next</span> →</button>
        </nav>

        <section
          className={`word-universe ${locked ? "is-locked" : ""}`}
          onPointerDown={(event) => {
            if (event.pointerType === "touch") setTouchStart({ x: event.clientX, y: event.clientY });
          }}
          onPointerUp={finishSwipe}
          onPointerCancel={() => setTouchStart(null)}
        >
          <div ref={wordContextPinRef} className="word-context-pin" data-visible={wordContextPinned} aria-hidden={!wordContextPinned}>
            <div className="word-context-pin-inner">
              <span>Current word</span>
              <strong>{active.word}</strong>
              <small>{activeEntry?.partOfSpeech || active.partOfSpeech}</small>
            </div>
          </div>
          <header ref={wordHeaderRef}>
            <div>
              <span>{activeEntry?.partOfSpeech || active.partOfSpeech}</span>
              <h1 id="word-discovery-title">{active.word}</h1>
              {activeEntry ? <p>{activeEntry.phonetic}</p> : null}
              {activeEntry ? <button type="button" className="pronunciation-guide-link" onClick={onOpenPronunciationGuide}>How to read this pronunciation</button> : null}
            </div>
            {activeEntry ? <button type="button" className="speak-button" onClick={() => onHear(activeEntry)}><span aria-hidden="true">▶</span> Hear</button> : null}
          </header>

          {activeEntry && !locked ? (
            <ol className="word-lesson-map" aria-label="What this word lesson gives you">
              <li><span>01</span>Understand</li>
              <li><span>02</span>Hear</li>
              <li><span>03</span>Distinguish</li>
              <li><span>04</span>See it used</li>
              <li><span>05</span>Choose how it lands</li>
            </ol>
          ) : null}

          {locked ? (
            <div className="word-plus-boundary">
              <span className="section-kicker">Verbalore Plus</span>
              <h3>The complete maintained word programme continues here.</h3>
              <p>Your 50 Free words remain fully usable. Plus opens the rest of the reviewed corpus, with the same meanings, pronunciation, examples, command practice, and learning record.</p>
              <button type="button" className="primary-action" onClick={onShowPlus}>See what Plus includes</button>
            </div>
          ) : loading ? (
            <div className="word-plus-boundary" aria-live="polite">
              <span className="section-kicker">Opening the word</span>
              <h3>Bringing its complete lesson into view.</h3>
              <p>The catalogue stays light. A complete Plus lesson arrives only when you choose it.</p>
            </div>
          ) : loadError || !activeEntry ? (
            <div className="word-plus-boundary" role="alert">
              <span className="section-kicker">Could not open this word</span>
              <h3>Your place is safe.</h3>
              <p>{loadError || "This maintained lesson is not available yet."}</p>
              <button type="button" className="secondary-action" onClick={() => onSelect(active.id)}>Try again</button>
            </div>
          ) : (
            <>
              <div className="word-core-meaning">
                <span className="section-kicker">In plain English</span>
                <p><SelectableProse text={activeEntry.meaning} surfaceId={`${active.id}-meaning`} /></p>
                {activeEntry.alternateMeaning ? <small>Another useful meaning: <SelectableProse text={activeEntry.alternateMeaning} surfaceId={`${active.id}-alternate-meaning`} /></small> : null}
              </div>

              <section className="word-worth">
                <span className="section-kicker">Why it is worth having</span>
                <p><SelectableProse text={activeEntry.whyWorthHaving} surfaceId={`${active.id}-worth`} /></p>
              </section>

              {activeEntry.distinction ? (
                <section className="word-distinction">
                  <span className="section-kicker">Not quite {activeEntry.distinction.nearbyWord}</span>
                  <p><SelectableProse text={activeEntry.distinction.explanation} surfaceId={`${active.id}-distinction`} /></p>
                </section>
              ) : null}

              {activeEntry.combinations.length ? (
                <section className="word-combinations">
                  <span className="section-kicker">Words it naturally keeps company with</span>
                  <div>{activeEntry.combinations.map((combination) => <span key={combination}>{combination}</span>)}</div>
                </section>
              ) : null}

              {activeEntry.relatedFamilyMembers?.length ? (
                <section className="word-related-family">
                  <span className="section-kicker">The same family, another useful move</span>
                  {activeEntry.relatedFamilyMembers.map((member) => (
                    <article
                      key={member.word}
                      id={`related-word-${member.word}`}
                      className={member.word === activeRelatedWord ? "is-addressed" : ""}
                      aria-current={member.word === activeRelatedWord ? "true" : undefined}
                    >
                      <h3>{member.word} <small>{member.partOfSpeech}</small></h3>
                      <p><SelectableProse text={member.meaning} surfaceId={`${active.id}-${member.word}-meaning`} /></p>
                      <blockquote>“<SelectableProse text={member.example} surfaceId={`${active.id}-${member.word}-example`} />”</blockquote>
                      <small>{member.relationship}</small>
                    </article>
                  ))}
                </section>
              ) : null}

              <section className="word-examples">
                <span className="section-kicker">See it living in different rooms</span>
                {activeEntry.examples.map((example) => (
                  <blockquote key={`${example.context}-${example.sentence}`}>
                    <span className="word-example-context">{example.context}</span>
                    <span className="word-example-sentence">“<SelectableProse text={example.sentence} surfaceId={`${active.id}-example-${example.context.toLocaleLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />”</span>
                  </blockquote>
                ))}
              </section>

              {activeEntry.landingRange ? (
                <section className="word-landing-range">
                  <span className="section-kicker">How the wording lands</span>
                  <h3><SelectableProse text={activeEntry.landingRange.situation} surfaceId={`${active.id}-landing-situation`} /></h3>
                  <p className="landing-invariant"><strong>Meaning held still:</strong> <SelectableProse text={activeEntry.landingRange.intention} surfaceId={`${active.id}-landing-intention`} /></p>
                  <div>
                    {activeEntry.landingRange.options.map((option) => (
                      <article key={`${option.label}-${option.line}`}>
                        <span>{option.label}</span>
                        <blockquote>“<SelectableProse text={option.line} surfaceId={`${active.id}-landing-${option.label}-line`} />”</blockquote>
                        <p><strong>It lands as:</strong> <SelectableProse text={option.effect} surfaceId={`${active.id}-landing-${option.label}-effect`} /></p>
                        <small><strong>Tradeoff:</strong> <SelectableProse text={option.tradeoff} surfaceId={`${active.id}-landing-${option.label}-tradeoff`} /></small>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}

              {activeEntry.articulationDimensions?.length ? (
                <section className="word-articulation-dimensions">
                  <span className="section-kicker">What changes when the landing changes</span>
                  <div>
                    {activeEntry.articulationDimensions.map((dimension) => (
                      <article key={dimension.option}>
                        <h3>{dimension.option}</h3>
                        <dl>
                          {Object.entries(dimension.changed).map(([name, explanation]) => (
                            <div key={name}>
                              <dt>{name}</dt>
                              <dd><SelectableProse text={explanation} surfaceId={`${active.id}-dimension-${dimension.option}-${name}`} /></dd>
                            </div>
                          ))}
                        </dl>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}

              {activeEntry.enrichmentMisfire ? (
                <section className="word-enrichment-misfire">
                  <span className="section-kicker">A tempting line that misses</span>
                  <blockquote>“<SelectableProse text={activeEntry.enrichmentMisfire.line} surfaceId={`${active.id}-enrichment-misfire-line`} />”</blockquote>
                  <p><strong>Why it tempts:</strong> <SelectableProse text={activeEntry.enrichmentMisfire.whyTempting} surfaceId={`${active.id}-enrichment-misfire-tempting`} /></p>
                  <p><strong>Why it misses:</strong> <SelectableProse text={activeEntry.enrichmentMisfire.whyWrong} surfaceId={`${active.id}-enrichment-misfire-wrong`} /></p>
                </section>
              ) : null}

              {activeEntry.nextSteps?.length ? (
                <section className="word-next-steps">
                  <span className="section-kicker">Keep building from here</span>
                  <div>
                    {activeEntry.nextSteps.map((step) => {
                      const target = entries.find((entry) => entry.word === step.target);
                      return (
                        <article key={`${step.kind}-${step.target}`}>
                          <span>{step.kind.replaceAll("-", " ")}</span>
                          <h3>{step.target}</h3>
                          <p><SelectableProse text={step.reason} surfaceId={`${active.id}-next-${step.target}`} /></p>
                          {target ? <button type="button" onClick={() => onSelect(target.id)}>Open this word →</button> : null}
                        </article>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              {activeEntry.commonMisuse ? <p className="word-misuse"><strong>Watch for:</strong> <SelectableProse text={activeEntry.commonMisuse} surfaceId={`${active.id}-misuse`} /></p> : null}

              <footer className="word-universe-actions">
                <button
                  type="button"
                  className="primary-action"
                  disabled={contextSaved}
                  onClick={() => onSave(activeEntry)}
                >
                  {contextSaved ? "Lesson saved" : saved ? "Save this lesson context" : "Add to My Words"}
                </button>
                <button type="button" className="secondary-action" onClick={() => onPractice(activeEntry)}>Practise this word</button>
              </footer>
              <p className="word-source-note">First met in <strong>{activeEntry.sourceReadingTitle}</strong>. Saving keeps that example with the word.</p>
            </>
          )}
        </section>

        <footer className="word-discovery-promise">
          <span className="eyebrow">Words worth having</span>
          <h2>Build a vocabulary <em>you can actually use.</em></h2>
          <p>Choose what the word should help you do, distinguish its shade, and practise how it changes a sentence.</p>
        </footer>
      </article>
    </section>
  );
}

function matchesCriteria(
  entry: CommandVocabularyCatalogEntry,
  criteria: WordDiscoveryCriteria,
) {
  return (criteria.initial === "all" || entry.word.startsWith(criteria.initial.toLocaleLowerCase()))
    && (criteria.handle === "all" || entry.handles.includes(criteria.handle))
    && (criteria.level === "all" || entry.difficulty === criteria.level)
    && (criteria.register === "all" || entry.registers.includes(criteria.register))
    && (criteria.articulation === "all" || entry.articulations.includes(criteria.articulation));
}

function levelLabel(level: CommandVocabularyCatalogEntry["difficulty"]) {
  if (level === "foundation") return "Free foundation";
  if (level === "bright-frontier") return "Recognisable, worth commanding";
  if (level === "mid-frontier") return "Elevated";
  return "Rare but live";
}

function ChoiceRow<T extends string>({
  className,
  label,
  choices,
  value,
  onChoose,
}: {
  className?: string;
  label: string;
  choices: Array<{ id: T; label: string }>;
  value: T;
  onChoose: (id: T) => void;
}) {
  return (
    <section className={`word-discovery-choice-row${className ? ` ${className}` : ""}`} aria-label={label}>
      <span>{label}</span>
      <div>
        {choices.map((choice) => (
          <button
            key={choice.id}
            type="button"
            className={value === choice.id ? "active" : ""}
            aria-pressed={value === choice.id}
            onClick={() => onChoose(choice.id)}
          >
            {choice.label}
          </button>
        ))}
      </div>
    </section>
  );
}
